import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faUserTie, faHouseUser } from "@fortawesome/free-solid-svg-icons";
import { register, verifyEmail, resendVerificationCode } from "../services/authService";  // Import resendVerificationCode
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "",
  });
  const [verificationCode, setVerificationCode] = useState("");  // Thêm state cho mã xác minh
  const [isRegistered, setIsRegistered] = useState(false); // Kiểm tra đã đăng ký thành công chưa
  const [touched, setTouched] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [animate, setAnimate] = useState(false);
  const [timer, setTimer] = useState(60); // Quản lý thời gian đếm ngược
const [canResend, setCanResend] = useState(false); // Cho phép gửi lại mã khi hết 60 giây


  // ===== Validate =====
  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = "Vui lòng nhập họ tên";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) e.email = "Email chưa hợp lệ";
    if (form.password.length < 8) e.password = "Mật khẩu tối thiểu 8 ký tự";
    if (!/^[0-9]{9,11}$/.test(form.phone)) e.phone = "Số điện thoại chưa hợp lệ (9-11 số)";
    if (!form.role) e.role = "Vui lòng chọn phân quyền";
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  // ===== Submit =====
  async function onSubmit(e) {
    e.preventDefault();
    setTouched({
      name: true,
      email: true,
      password: true,
      phone: true,
      role: true,
    });
    if (!isValid) return;
    setSubmitting(true);

    try {
      await register(form);
      setRegisterError("");
      setIsRegistered(true);  // Đặt trạng thái đăng ký thành công
      toast.success(" Đăng ký thành công! Vui lòng kiểm tra email để xác minh.", {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        theme: "colored",
        style: {
          background: "#3b5998",
          color: "#fff",
          fontWeight: 600,
          borderRadius: "12px",
        },
      });
    } catch (error) {
      const message = error?.error || error?.response?.data?.error || error?.message || "Đăng ký thất bại!";
      setRegisterError(message);
      toast.error(message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        theme: "colored",
        style: {
          background: "#111",
          color: "#fff",
          fontWeight: 600,
          borderRadius: "12px",
        },
      });
    } finally {
      setSubmitting(false);
    }
  }

async function onVerifyEmail(e) {
  e.preventDefault();
  setSubmitting(true);

  try {
    if (timer <= 0) {
      toast.error("Mã xác minh đã hết hạn. Vui lòng yêu cầu mã mới.", {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        theme: "colored",
        style: {
          background: "#FF4B4B",
          color: "#fff",
          fontWeight: 600,
          borderRadius: "12px",
        },
      });
      return; // Ngừng quá trình xác minh nếu mã đã hết hạn
    }

    // Gửi yêu cầu xác minh mã
    await verifyEmail(form.email, verificationCode);
    toast.success("Xác minh email thành công!", {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: false,
      theme: "colored",
      style: {
        background: "#4BB543",
        color: "#fff",
        fontWeight: 600,
        borderRadius: "12px",
      },
      onClose: () => navigate("/login"),
    });
  } catch (error) {
    toast.error(error?.response?.data?.error || "Xác minh email thất bại!", {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: false,
      theme: "colored",
      style: {
        background: "#FF4B4B",
        color: "#fff",
        fontWeight: 600,
        borderRadius: "12px",
      },
    });
  } finally {
    setSubmitting(false);
  }
}



async function onResendVerificationCode() {
  if (!canResend) return;  // Nếu chưa hết 60 giây, không cho gửi lại mã

  setSubmitting(true);

  try {
    // Gửi yêu cầu gửi lại mã xác minh
    await resendVerificationCode(form.email);

    toast.success("Mã xác minh đã được gửi lại!", {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: false,
      theme: "colored",
      style: {
        background: "#4BB543",
        color: "#fff",
        fontWeight: 600,
        borderRadius: "12px",
      },
    });

    // Reset bộ đếm ngược và cho phép gửi lại mã
    setTimer(60);  // Đặt lại thời gian đếm ngược
    setCanResend(false);  // Vô hiệu hóa nút gửi lại mã trong 60 giây tiếp theo
  } catch (error) {
    toast.error(error?.response?.data?.error || "Đã có lỗi khi gửi lại mã xác minh!", {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: false,
      theme: "colored",
      style: {
        background: "#FF4B4B",
        color: "#fff",
        fontWeight: 600,
        borderRadius: "12px",
      },
    });
  } finally {
    setSubmitting(false);
  }
}


useEffect(() => {
  // Đảm bảo rằng khi `timer` bằng 0, người dùng có thể gửi lại mã
  if (timer > 0) {
    const interval = setInterval(() => {
      setTimer(prevTimer => prevTimer - 1);
    }, 1000);

    return () => clearInterval(interval); // Dọn dẹp interval khi component bị unmount hoặc timer đạt 0
  } else {
    setCanResend(true); // Sau 60 giây, cho phép gửi lại mã
  }
}, [timer]); // Chạy lại effect khi `timer` thay đổi

useEffect(() => {
  // Khi component lần đầu được mount, timer sẽ luôn bắt đầu là 60 giây
  setTimer(60);
}, []); 


  useEffect(() => {
    setTimeout(() => setAnimate(true), 200);
  }, []);

  return (
    <div style={styles.page}>
      {/* Left Pane */}
      <div style={styles.leftPane}>
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1500&q=80"
          alt="Luxury room"
          style={styles.image}
        />
        <div style={styles.overlay}></div>
        <div style={{ ...styles.textBlock, ...(animate ? styles.fadeInUp : styles.hidden) }}>
          <h2 style={styles.subText}>SHome</h2>
          <h1 style={styles.mainText}>
            <span style={styles.gradientText}>Find Your Space</span>
          </h1>
          <p style={styles.tagline}>
            Tạo tài khoản để bắt đầu hành trình tìm không gian sống lý tưởng của bạn.
          </p>
          <Link to="/" style={{ textDecoration: "none" }}>
            <button style={styles.ctaBtn}>Khám phá ngay →</button>
          </Link>
        </div>
      </div>

      {/* Right Pane */}
      <div style={styles.rightPane}>
        <form
          style={{ ...styles.form, ...(animate ? styles.scaleIn : styles.hidden) }}
          onSubmit={isRegistered ? onVerifyEmail : onSubmit}  // Đảm bảo chỉ gọi onSubmit cho đăng ký
        >
          <h2 style={styles.title}>{isRegistered ? "Xác minh email" : "Tạo tài khoản"}</h2>

          {!isRegistered && registerError && (
            <div role="alert" style={styles.registerError}>
              {registerError}
            </div>
          )}

          {!isRegistered ? (
            <>
              <FormField
                label="Họ và tên"
                id="name"
                type="text"
                value={form.name}
                placeholder="Nguyễn Văn A"
                touched={touched.name}
                error={errors.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onBlur={() => setTouched({ ...touched, name: true })}
              />

              <FormField
                label="Email"
                id="email"
                type="email"
                value={form.email}
                placeholder="you@example.com"
                touched={touched.email}
                error={errors.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                onBlur={() => setTouched({ ...touched, email: true })}
              />

              <FormField
                label="Số điện thoại"
                id="phone"
                type="text"
                value={form.phone}
                placeholder="0123456789"
                touched={touched.phone}
                error={errors.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                onBlur={() => setTouched({ ...touched, phone: true })}
              />

              <div style={styles.field}>
                <label style={styles.label} htmlFor="password">
                  Mật khẩu
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    style={{
                      ...styles.input,
                      ...(touched.password && errors.password ? styles.inputError : {}),
                    }}
                    type={showPwd ? "text" : "password"}
                    id="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    onBlur={() => setTouched({ ...touched, password: true })}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} style={styles.eyeBtn}>
                    <FontAwesomeIcon icon={showPwd ? faEyeSlash : faEye} />
                  </button>
                </div>
                {touched.password && errors.password && <span style={styles.error}>{errors.password}</span>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Phân quyền</label>
                <div style={styles.roleWrapper}>
                  <label
                    style={{
                      ...styles.roleBox,
                      ...(form.role === "CTV" ? styles.roleBoxActive : {}),
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.role === "CTV"}
                      onChange={() => setForm({ ...form, role: "CTV" })}
                      style={{ display: "none" }}
                    />
                    <div style={styles.inlineContent}>
                      <FontAwesomeIcon
                        icon={faUserTie}
                        style={{
                          fontSize: 16,
                          marginRight: 8,
                          transition: "transform 0.3s ease",
                          transform: form.role === "CTV" ? "scale(1.2)" : "scale(1)",
                          color: form.role === "CTV" ? "#fff" : "#333",
                        }}
                      />
                      <span style={styles.roleText}>Cộng tác viên</span>
                    </div>
                  </label>

                  <label
                    style={{
                      ...styles.roleBox,
                      ...(form.role === "boss" ? styles.roleBoxActive : {}),
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.role === "boss"}
                      onChange={() => setForm({ ...form, role: "boss" })}
                      style={{ display: "none" }}
                    />
                    <div style={styles.inlineContent}>
                      <FontAwesomeIcon
                        icon={faHouseUser}
                        style={{
                          fontSize: 16,
                          marginRight: 8,
                          transition: "transform 0.3s ease",
                          transform: form.role === "boss" ? "scale(1.2)" : "scale(1)",
                          color: form.role === "boss" ? "#fff" : "#333",
                        }}
                      />
                      <span style={styles.roleText}>Người cho thuê</span>
                    </div>
                  </label>
                </div>
                {touched.role && errors.role && <span style={styles.error}>{errors.role}</span>}
              </div>

              <button
                type="submit"
                disabled={!isValid || submitting}
                style={styles.submit}
              >
                {submitting ? "Đang tạo tài khoản..." : "Tạo tài khoản"}
              </button>

              <p style={styles.switch}>
                Bạn đã có tài khoản?{" "}
                <Link to="/login" style={styles.link}>
                  Đăng nhập
                </Link>
              </p>
            </>
          ) : (
            <>
              <FormField
                label="Mã xác minh"
                id="verificationCode"
                type="text"
                value={verificationCode}
                placeholder="Nhập mã xác minh"
                touched={touched.verificationCode}
                error={errors.verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                onBlur={() => setTouched({ ...touched, verificationCode: true })}
              />

              <button
                type="submit"
                disabled={!verificationCode || submitting}
                style={styles.submit}
              >
                {submitting ? "Đang xác minh..." : "Xác minh"}
              </button>

              {canResend ? (
  <button
    type="button"
    onClick={onResendVerificationCode}
    style={styles.submitResend}
    disabled={submitting}
  >
    {submitting ? "Đang gửi lại mã..." : "Gửi lại mã xác minh"}
  </button>
) : (
  <p style={styles.timerText}>Còn lại {timer}s để gửi lại mã.</p>
)}

            </>
          )}
        </form>
      </div>

      <ToastContainer />
    </div>
  );
}

// ================== FormField Component ==================
function FormField({
  label,
  id,
  type,
  value,
  placeholder,
  touched,
  error,
  onChange,
  onBlur,
}) {
  return (
    <div style={styles.field}>
      <label style={styles.label} htmlFor={id}>
        {label}
      </label>
      <div style={styles.inputWrapper}>
        <input
          style={{
            ...styles.input,
            ...(touched && error ? styles.inputError : {}),
          }}
          type={type}
          id={id}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
        />
      </div>
      {touched && error && <span style={styles.error}>{error}</span>}
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    height: "100vh",
    fontFamily: "'Inter', sans-serif",
    background: "#121212",
    color: "#333",
    overflow: "hidden",
  },
  leftPane: { flex: "0 0 55%", position: "relative", overflow: "hidden" },
  rightPane: {
    flex: "0 0 45%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#fff",
    padding: 20,
  },
  image: { width: "100%", height: "100%", objectFit: "cover" },
  overlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
  },
  textBlock: {
    position: "absolute",
    left: 40,
    top: "50%",
    transform: "translateY(-50%)",
    maxWidth: "80%",
  },
  hidden: { opacity: 0, transform: "scale(0.95) translateY(20px)" },
  fadeInUp: {
    opacity: 1,
    transform: "translateY(-50%)",
    transition: "all 0.8s ease-out",
  },
  scaleIn: { opacity: 1, transform: "scale(1)", transition: "all 0.6s ease-out" },
  subText: { fontSize: 20, fontWeight: 600, color: "#3b5998", margin: 0 },
  mainText: {
    fontSize: 42,
    fontWeight: 800,
    marginTop: 8,
    marginBottom: 12,
    color: "#fff",
  },
  gradientText: {
    background: "white",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  tagline: { fontSize: 16, color: "#f3f4f6", marginBottom: 20, lineHeight: 1.5 },
  ctaBtn: {
    padding: "10px 22px",
    borderRadius: 25,
    border: "none",
    background: "#3b5998",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all .3s",
  },
  form: {
    width: "100%",
    maxWidth: 420,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    background: "#fff",
    padding: "28px 32px",
    borderRadius: 14,
    boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    marginBottom: 6,
    color: "#121212",
    textAlign: "center",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 14, color: "#444", marginLeft: 4 },
  inputWrapper: { position: "relative", display: "flex", alignItems: "center" },
  input: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: "1px solid #ddd",
    background: "#fafafa",
    padding: "0 40px 0 14px",
    color: "#333",
    fontSize: 15,
    outline: "none",
    width: "100%",
    transition: "all .25s",
  },
  inputError: { borderColor: "#ff4d4d" },
  error: { fontSize: 12, color: "#ff4d4d", marginLeft: 4 },
  registerError: {
    padding: "10px 12px",
    border: "1px solid #fecaca",
    borderRadius: 8,
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 600,
  },
  eyeBtn: {
    position: "absolute",
    top: 14,
    right: 14,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#888",
    fontSize: 16,
  },
  submit: {
    height: 48,
    borderRadius: 12,
    border: "none",
    background: "#3b5998",
    color: "white",
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 10,
    transition: "transform .25s",
  },
  submitResend: {
    height: 48,
    borderRadius: 12,
    border: "1px solid black",
    background: "white",
    color: "black",
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 10,
    transition: "transform .25s",
  },
  switch: {
    marginTop: 16,
    fontSize: 14,
    textAlign: "center",
    color: "#666",
  },
  link: {
        color: "#3b5998",
    textDecoration: "none",
    cursor: "pointer",
    fontWeight: 600,
  },
  roleWrapper: { display: "flex", gap: 14, marginTop: 6 },
  roleBox: {
    flex: 1,
    padding: "12px 16px",
    borderRadius: 10,
    border: "1px solid #ccc",
    background: "#fafafa",
    color: "#444",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.25s ease",
  },
  roleBoxActive: {
    background: "#3b5998",
    color: "#fff",
    borderColor: "#3b5998",
    transform: "scale(1.05)",
  },
  inlineContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  roleText: { fontSize: 14, fontWeight: 500 },
  timerText: {
  fontSize: 14,
  color: "#888",
  textAlign: "center",
  marginTop: "10px",
},

};


